<template>
    <left-stripe-layout
        class="d-flex flex-column justify-center" 
        :color="announcement.color"
    >
        <Header 
            class="header"
            :large="false" 
            :two-line="false"
            :color="announcement.color"
            :icon="announcement.iconName"
            :text="displayShort && shortTitle ? shortTitle : title"
        ></Header>
        <div class="announcement-content">
            <p class="announcement-text" v-if="departures.length === 0">Aktuálně nejsou dostupné žádné odjezdy linek...</p>
            <div v-else>
                <table v-if="isLandscape" class="table">
                    <col width="15%" />
                    <col v-if="!(hideStopColumn && stops.length === 1)" width="30%" />
                    <col width="40%" />
                    <col width="15%" />
                    <tr>
                        <th v-for="column in columns" class="table-head" :key="`head-${column}`">
                            {{column}}
                        </th>
                    </tr>
                    <tr v-for="n in maxItems" :key="`stop-${n}`">
                        <td>
                            <v-icon
                                class="mr-2"
                                size="1.4vw"
                                :color="getDeparture(n).wheelchairAccessible && getDeparture(n).wheelchairBoarding ? announcement.color : 'none'"
                            >
                                accessible
                            </v-icon>
                            {{(getDeparture(n).routeShortName || '').padEnd(2, '')}}
                        </td>
                        <td v-if="!(hideStopColumn && stops.length === 1)">
                            <Marquee v-if="getDeparture(n).stopName" padding="0px" :items="[getDeparture(n).stopName]" :fixedAnimationDuration="refreshPeriod * 1000"/>
                        </td>
                        <td>
                            <Marquee v-if="getDeparture(n).tripHeadsign" padding="0px" :items="[getDeparture(n).tripHeadsign]" :fixedAnimationDuration="refreshPeriod*1000"/>
                        </td>
                        <td v-if="getDeparture(n).minutesToDeparture === 0" class="blink">**</td>
                        <td v-else>{{getDeparture(n).minutesToDeparture &lt;= minutesToDepartureLimit ? `${getDeparture(n).minutesToDeparture} min.` : getDeparture(n).departureTime}}</td>
                    </tr>
                </table>
                <table v-else class="portrait-table">
                    <col width="11%" />
                    <col v-if="!(hideStopColumn && stops.length === 1)" width="39%" />
                    <col width="28%" />
                    <col width="22%" />
                    <tr>
                        <th v-for="(column, idx) in columns" :class="`table-head ${idx == 3 - Number(hideStopColumn) ? 'text-center' : ''}`" :key="`head-${column}`">
                            {{column}}
                        </th>
                    </tr>
                    <tr v-for="n in maxItems" :key="`stop-${n}`">
                        <td :style="{color: getDeparture(n).wheelchairAccessible && getDeparture(n).wheelchairBoarding ? announcement.color : 'white'}">{{getDeparture(n).routeShortName}}</td>
                        <td v-if="!(hideStopColumn && stops.length === 1)">
                            <Marquee v-if="getDeparture(n).stopName" padding="0px" :items="[getDeparture(n).stopName]" :fixedAnimationDuration="refreshPeriod*1000"/>
                        </td>
                        <td>
                            <Marquee v-if="getDeparture(n).tripHeadsign" padding="0px" :items="[getDeparture(n).tripHeadsign]" :fixedAnimationDuration="refreshPeriod*1000"/>
                        </td>
                        <td v-if="getDeparture(n).minutesToDeparture === 0" class="blink">**</td>
                        <td v-else>{{getDeparture(n).minutesToDeparture &lt;= minutesToDepartureLimit ? `${getDeparture(n).minutesToDeparture} min.` : getDeparture(n).departureTime}}</td>
                    </tr>
                </table>
            </div>
        </div>
    </left-stripe-layout>
</template>

<script>
import {config} from "../../../config";
import Header from '../../../components/Header';
import LeftStripeLayout from './Layouts/LeftStripeLayout.vue';
import Marquee from '../../../components/Marquee';

export default {
    name: "PublicTransportService",

    props: {
        announcement: Object,
        isLandscape: Boolean,
        isParallel: Boolean,
        refreshPeriod: Number,
        isPreview: Boolean
    },

    data() {
        return {
            minutesToDepartureLimit: 10,
            cz: config.cz
        }
    },

    computed: {
        maxItems() {
            return Math.min(this.isParallel && this.isLandscape ? 6 : 11);
        },

        departures() {
            return (this.isPreview 
                ? this.previewDepartures(this.announcement.serviceConfiguration && this.announcement.serviceConfiguration.stops ? this.announcement.serviceConfiguration.stops : []) 
                : JSON.parse(this.announcement.text).departures).slice(0, this.maxItems);
        },

        hideStopColumn() {
            return this.announcement.serviceConfiguration && (typeof this.announcement.serviceConfiguration === 'string' ? JSON.parse(this.announcement.serviceConfiguration) : this.announcement.serviceConfiguration).hideStopColumn;
        },

        stops() {
            return this.announcement.serviceConfiguration ? (typeof this.announcement.serviceConfiguration === 'string' ? JSON.parse(this.announcement.serviceConfiguration) : this.announcement.serviceConfiguration).stops : [];
        },

        columns() {
            return this.announcement.language === this.cz 
                ? [ !this.isLandscape ? '' : 'Linka', ...(this.hideStopColumn ? [] : ['Zastávka']), 'Směr', 'Odjezd' ] 
                : [ !this.isLandscape ? '' : 'Route', ...(this.hideStopColumn ? [] : ['Stop']), 'Direction', 'Leaving' ];
        },

        displayShort() {
            return this.isParallel;
        },

        // change in these texts needs to be reflected in backend service
        hideStopColumnShortTitle() {
            return `${this.announcement.language === this.cz ? 'MHD - ' : 'Stop: '}${this.stops[0]}`;
        },

        // change in these texts needs to be reflected in backend service
        hideStopColumnTitle() {
            return `${this.announcement.language === this.cz ? 'Odjezdy ze zastávky' : 'Departures from'} ${this.stops[0]}`;
        },

        shortTitle() {
            return this.stops.length === 1 && this.hideStopColumn ? this.hideStopColumnShortTitle : this.announcement.shortTitle;
        },

        title() {
            return this.stops.length === 1 && this.hideStopColumn ? this.hideStopColumnTitle : this.announcement.title;
        }
    },

    components: {
        Header,
        LeftStripeLayout,
        Marquee,
    },

    methods: {
        getDeparture(index) {
            return this.departures.length >= index ? { ...this.departures[index - 1], tripHeadsign: this.departures[index - 1].tripHeadsign.replaceAll('"', '') } : {} 
        },

        isImportant(index) {
            return this.departures[index - 1] && this.departures[index - 1].isImportant;
        },

        previewDepartures(stops) { 
            return stops.length > 0 ? [
                { departureTime: '08:00', minutesToDeparture: 0, routeShortName: '9', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Náhledová', tripHeadsign: 'Juliánov'},
                { departureTime: '08:05', minutesToDeparture: 5, routeShortName: '12', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Obřany'},
                { departureTime: '08:10', minutesToDeparture: 10, routeShortName: '11', wheelchairAccessible: true, wheelchairBoarding: false, stopName: 'Náhledová', tripHeadsign: 'Řečkovice'},
                { departureTime: '08:11', minutesToDeparture: 11, routeShortName: '9', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Náhledová', tripHeadsign: 'Juliánov'},
                { departureTime: '08:25', minutesToDeparture: 25, routeShortName: '10', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Obřany'},
                { departureTime: '08:50', minutesToDeparture: 50, routeShortName: '11', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Řečkovice'},
                { departureTime: '08:55', minutesToDeparture: 55, routeShortName: '9', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Náhledová', tripHeadsign: 'Juliánov'},
                { departureTime: '09:05', minutesToDeparture: 65, routeShortName: '11', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Obřany'},
                { departureTime: '09:10', minutesToDeparture: 70, routeShortName: '63', wheelchairAccessible: true, wheelchairBoarding: false, stopName: 'Náhledová', tripHeadsign: 'Řečkovice'},
                { departureTime: '09:25', minutesToDeparture: 85, routeShortName: '9', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Náhledová', tripHeadsign: 'Juliánov'},
                { departureTime: '09:35', minutesToDeparture: 95, routeShortName: '10', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Obřany'},
                { departureTime: '09:55', minutesToDeparture: 115, routeShortName: '9', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Náhledová', tripHeadsign: 'Juliánov'},
                { departureTime: '10:05', minutesToDeparture: 125, routeShortName: '11', wheelchairAccessible: true, wheelchairBoarding: true, stopName: 'Testovací', tripHeadsign: 'Obřany'},
                { departureTime: '10:10', minutesToDeparture: 130, routeShortName: '63', wheelchairAccessible: true, wheelchairBoarding: false, stopName: 'Náhledová', tripHeadsign: 'Řečkovice'},
            ] : []
        },
    }
}
</script>

<style scoped>
    .center-cell {
        text-align: center;
    }

    .announcement {
        background: black;
        color: white;
        overflow: hidden;
        font-family: 'Roadgeek';
    }

    .announcement-text {
        font-size: 1.7vw;
    }

    .single .announcement-text {
        font-size: 2vw;
    }

    .single .header {
        margin-top: 3%;
        margin-bottom: 2%;
    }

    .announcement-content {
        width: 100%;
        height: 100%;
        display: flex;
        flex-flow: row;
        justify-content: space-between;
    }

    .announcement-content > div {
        width: 100%;
        height: 100%;
    }

    .table {
        width: 100%;
        height: 97%;
        font-size: 1.9vw;
        text-align: left;
        table-layout: fixed;
    }

    tr {
        height: 4.25vh
    }

    .table-head {
        padding-bottom: 0.25vh;
    }

    .parallel .table {
        font-size: 1.4vw;
    }

    .portrait-table {
        width: 107%;
        height: 97%;
        font-size: 1.8vw;
        text-align: left;
        table-layout: fixed;
    }

    .blink {
        animation: blinker 1s step-start infinite;
    }

    @keyframes blinker {
        50% { opacity: 0; }
    }
</style>
